import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jobApplyUrl } from '@goyone/shared';
import { Icon } from '../../components/Icon';
import { Logo } from '../../components/Logo';
import { api } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';

interface CareerJob {
  slug: string;
  title: string;
  department: string | null;
  employmentLabel: string;
  workModeLabel: string | null;
  location: string | null;
  openings: number;
  closesOn: string | null;
  experienceMin: number | null;
  salary: { min: number | null; max: number | null } | null;
}

interface CareersPage {
  tenantName: string;
  tenantSlug: string;
  logoUrl: string | null;
  brandColor: string | null;
  jobs: CareerJob[];
}

/**
 * صفحة وظائف الشركة.
 *
 * ## ولماذا صفحةٌ تجمعها لا رابطٌ لكلّ وظيفة
 *
 * روابطُ الوظائف تُنشر مفرّقةً في منشورات، فمن رأى إعلانًا لوظيفةٍ لا
 * تناسبه لا يعرف أنّ عند الشركة غيرَها — وقد يكون هو من تبحث عنه في
 * الوظيفة التي بجانبها. والصفحةُ الواحدةُ رابطٌ ثابتٌ يُكتب في توقيع
 * البريد وفي صفحة الشركة على المنصّات، ويبقى صالحًا بعد أن تُغلق كلُّ
 * وظيفةٍ فيه اليوم.
 *
 * ## وما ليس فيها
 *
 * لا مراجعاتِ موظّفين ولا صورَ مكتبٍ ولا مدوّنة: تلك تحتاج محرّرَ محتوًى
 * وليست في هذا النظام. وما هنا صادقٌ صغير — من الشركة، وما هو مفتوحٌ
 * الآن، وكيف يُتقدَّم.
 */
export function PublicCareers() {
  const { tenant = '' } = useParams();

  const page = useQuery({
    queryKey: ['careers', tenant],
    queryFn: () =>
      api<CareersPage>(`/public/jobs/${encodeURIComponent(tenant)}`, {
        skipAuth: true,
      }),
    retry: false,
  });

  if (page.isPending) {
    return (
      <Frame>
        <div className="skeleton" style={{ height: 220 }} />
      </Frame>
    );
  }

  if (page.isError) {
    return (
      <Frame>
        <div className="book-done">
          <h1 className="book-title">الصفحة غير موجودة</h1>
          <p className="mute">
            {page.error instanceof Error
              ? page.error.message
              : 'هذا الرابط لا يخصّ شركةً في النظام.'}
          </p>
        </div>
      </Frame>
    );
  }

  const info = page.data;
  const brand = info.brandColor
    ? ({ ['--brand']: info.brandColor } as React.CSSProperties)
    : undefined;

  return (
    <Frame brandStyle={brand} wide>
      <header className="book-brand">
        {info.logoUrl ? (
          <img className="book-logo" src={info.logoUrl} alt={info.tenantName} />
        ) : (
          <span className="book-logo book-logo-fallback">
            {info.tenantName.slice(0, 2)}
          </span>
        )}
        <div>
          <div className="book-tenant">{info.tenantName}</div>
          <h1 className="book-title">الوظائف المفتوحة</h1>
        </div>
      </header>

      <p className="mute">
        {info.jobs.length === 0
          ? 'لا وظائف مفتوحة الآن.'
          : `${plural(info.jobs.length, WORDS.job)} مفتوحة الآن`}
      </p>

      {/*
        الصفحةُ الفارغةُ تُقال ولا تُترك بيضاء: من فتح الرابط يريد أن يعرف
        أنّه وصل إلى المكان الصحيح وأنّ لا شيءَ اليوم — لا أن يظنّه معطوبًا.
      */}
      {info.jobs.length === 0 ? (
        <section className="book-done mt-4">
          <p className="mute">
            لا وظائف مفتوحة في {info.tenantName} الآن. احفظ هذا الرابط — هو
            نفسُه يُحدَّث حين تُفتح وظيفةٌ جديدة.
          </p>
        </section>
      ) : (
        <section className="rc-cards mt-4">
          {info.jobs.map((j) => (
            <a
              key={j.slug}
              className="rc-card"
              href={jobApplyUrl(window.location.origin, info.tenantSlug, j.slug)}
            >
              <strong>{j.title}</strong>

              <div className="book-meta">
                <span className="rc-chip">{j.employmentLabel}</span>
                {j.workModeLabel && (
                  <span className="rc-chip">{j.workModeLabel}</span>
                )}
                {j.department && <span className="rc-chip">{j.department}</span>}
                {j.location && <span className="rc-chip">{j.location}</span>}
                {j.salary && (
                  <span className="rc-chip">{salaryText(j.salary)}</span>
                )}
                {j.experienceMin !== null && (
                  <span className="rc-chip">
                    خبرة {num(j.experienceMin)} سنة فأكثر
                  </span>
                )}
              </div>

              <span className="tiny mute">
                {/*
                  عددُ الشواغر يُقال حين يزيد على واحد: «شاغرٌ واحد» ضجيجٌ
                  على كلّ بطاقة، و«ثلاثة شواغر» خبرٌ يرفع احتمالَ التقديم.
                */}
                {j.openings > 1 && `${plural(j.openings, WORDS.vacancy)} · `}
                {j.closesOn
                  ? `التقديم حتى ${date(j.closesOn)}`
                  : 'التقديم مفتوح'}
              </span>

              <span className="tiny" style={{ color: 'var(--brand)' }}>
                <Icon name="arrowLeft" size={12} /> اقرأ التفاصيل وقدّم
              </span>
            </a>
          ))}
        </section>
      )}

      <p className="tiny mute mt-4">
        التقديمُ لا يحتاج حسابًا ولا كلمةَ سرّ. وما تكتبه يصل إلى فريق
        الموارد البشرية في {info.tenantName} مباشرةً.
      </p>
    </Frame>
  );
}

/** نطاقُ الراتب مقروءًا — وحدٌّ واحدٌ يُقرأ «من» أو «حتى» لا نصفَ نطاق. */
function salaryText(s: { min: number | null; max: number | null }): string {
  const fmt = (n: number) => n.toLocaleString('ar-EG');
  if (s.min !== null && s.max !== null) return `${fmt(s.min)} — ${fmt(s.max)}`;
  if (s.min !== null) return `من ${fmt(s.min)}`;
  if (s.max !== null) return `حتى ${fmt(s.max)}`;
  return '';
}

/**
 * الإطار.
 *
 * صفحةُ الشركة أمام من يطرق بابها لا صفحتُنا: بلا شريطٍ علويّ ولا قائمة،
 * وبشعارها ولونها.
 */
function Frame({
  children,
  brandStyle,
  wide,
}: {
  children: React.ReactNode;
  brandStyle?: React.CSSProperties;
  wide?: boolean;
}) {
  return (
    <div className="book-page" style={brandStyle}>
      {/*
        الإطارُ هو إطارُ صفحة التقديم نفسُه — و`book-card-wide` متروكةٌ
        عمدًا: تلك مصنوعةٌ لهيكل الحجز ذي العمودين فتُلغي الحشو وتقصّ ما زاد.
      */}
      <div className={`book-card${wide ? ' rc-apply-card' : ''}`}>
        {children}
      </div>
      <p className="book-foot tiny mute">
        <Link className="tiny mute" to="/">
          <Logo size={16} /> GoyOne
        </Link>
      </p>
    </div>
  );
}
